import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useOrganizationScope } from "../../contexts/OrganizationScopeContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTheme } from "../../contexts/ThemeContext";
import { LanguageSwitcher } from "../ui/LanguageSwitcher";
import { ThemeToggle } from "../ui/ThemeToggle";
import CriticalBulletinBanner from "../CriticalBulletinBanner";
import BulletinNotificationBadge from "../BulletinNotificationBadge";
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  Plane, 
  Battery, 
  FileText, 
  AlertTriangle, 
  FolderOpen,
  Target,
  LogOut,
  ChevronDown,
  ChevronRight,
  Radar,
  FileBarChart,
  ClipboardList,
  BarChart3,
  Wrench,
  Bell,
  ShieldCheck,
  Calendar,
  CalendarDays,
  Clock,
  BookOpen,
  Shield,
  Award,
  Send,
  Mail,
  Menu,
  X,
  Map
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const ALL_ORGANIZATIONS_VALUE = "all-organizations";

// Build time in the browser's local time: "14. 10. 2026 15:42" (cs) or "14 Oct 2026 15:42" (en).
const formatBuildTime = (value, language) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const locale = language === "cs" ? "cs-CZ" : "en-GB";
  const day = date.toLocaleDateString(locale, {
    day: "numeric",
    month: language === "cs" ? "numeric" : "short",
    year: "numeric",
  });
  const time = date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  return `${day} ${time}`;
};

export const DashboardLayout = ({ children }) => {
  const { user, logout, appVersion } = useAuth();
  const {
    activeOrganizationId,
    clearScope,
    isPlatformAdmin,
    loading: organizationScopeLoading,
    organizations,
    setActiveOrganization,
  } = useOrganizationScope();
  const { t, language, translate } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedModules, setExpandedModules] = useState({
    flightOps: true,
    equipment: false,
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const buildTime = appVersion?.builtAt ? formatBuildTime(appVersion.builtAt, language) : null;
  const appVersionLabel = appVersion
    ? [
        translate("appVersion.version", { version: appVersion.version }),
        buildTime && translate("appVersion.built", { date: buildTime }),
      ].filter(Boolean).join(" · ")
    : null;

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const toggleModule = (module) => {
    setExpandedModules(prev => ({
      ...prev,
      [module]: !prev[module]
    }));
  };

  const isModuleActive = (paths) => {
    return paths.some(path => location.pathname.startsWith(path));
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  };

  const getRoleBadge = (role) => {
    // Use translated role labels from t.roles
    return t.roles?.[role] || role;
  };

  // Module definitions with sub-items
  const modules = [
    {
      key: "intelligence",
      label: t.nav.intelligence,
      icon: BarChart3,
      path: "/dashboard",
      standalone: true
    },
    {
      key: "flightOps",
      label: t.nav.flightOps,
      icon: FileText,
      expandable: true,
      paths: ["/missions", "/daily-ops", "/scenarios", "/fleet-map"],
      items: [
        { path: "/daily-ops", label: t.subNav?.dailyOps || "Daily Ops", icon: Calendar },
        { path: "/missions", label: t.subNav.missions, icon: ClipboardList },
        { path: "/missions/calendar", label: t.subNav?.missionCalendar || "Mission Calendar", icon: CalendarDays },
        { path: "/missions/history", label: t.subNav?.missionHistory || "Mission History", icon: Clock },
        { path: "/fleet-map", label: t.nav?.fleetMap || "Fleet Map", icon: Map },
        { path: "/scenarios", label: t.subNav.scenarios, icon: Target }
      ]
    },
    {
      key: "pilots",
      label: t.subNav.pilots,
      icon: Users,
      path: "/pilots",
      standalone: true
    },
    {
      key: "equipment",
      label: t.nav.equipment,
      icon: Plane,
      expandable: true,
      paths: ["/aircraft", "/aircraft/calendar", "/batteries"],
      items: [
        { path: "/aircraft", label: t.subNav.aircraft, icon: Plane },
        { path: "/aircraft/calendar", label: t.aircraft?.calendar?.title || (language === 'cs' ? 'Kalendář' : 'Calendar'), icon: Calendar },
        { path: "/batteries", label: t.subNav.batteries, icon: Battery }
      ]
    },
    {
      key: "incidents",
      label: t.subNav.incidents,
      icon: AlertTriangle,
      path: "/incidents",
      standalone: true
    },
    {
      key: "documents",
      label: t.subNav.documents,
      icon: FolderOpen,
      path: "/documents",
      standalone: true
    },
    {
      key: "reports",
      label: t.nav.reports,
      icon: FileBarChart,
      path: "/reports",
      standalone: true,
      roles: ["platform_admin", "organization_admin", "compliance_manager"]
    },
    {
      key: "news",
      label: t.nav?.news || "News & Regulations",
      icon: Bell,
      path: "/news",
      standalone: true
    },
    {
      key: "insurance",
      label: t.nav?.insurance || "Pojištění",
      icon: Shield,
      path: "/insurance",
      standalone: true,
      roles: ["platform_admin", "organization_admin"]
    },
    {
      key: "submissions",
      label: t.nav?.submissions || "Podání ÚCL",
      icon: Send,
      path: "/submissions",
      standalone: true,
      roles: ["platform_admin", "organization_admin"]
    },
    {
      key: "authorizations",
      label: t.nav?.authorizations || "Autorizace",
      icon: Award,
      path: "/authorizations",
      standalone: true,
      roles: ["platform_admin", "organization_admin", "compliance_manager"]
    },
    {
      key: "correspondence",
      label: t.nav?.correspondence || "Korespondence",
      icon: Mail,
      path: "/correspondence",
      standalone: true,
      roles: ["platform_admin", "organization_admin", "compliance_manager"]
    }
  ];

  // Admin-only items
  const adminItems = [
    { path: "/organizations", label: t.nav.organizations, icon: Building2, roles: ["platform_admin"] },
    { path: "/bulletins", label: t.nav?.bulletins || "Bulletins", icon: Bell, roles: ["platform_admin"] },
    { path: "/audits", label: t.nav?.auditSimulation || "Audit Simulation", icon: ShieldCheck, roles: ["platform_admin", "organization_admin", "compliance_manager"] },
    { path: "/knowledge-base", label: t.nav?.knowledgeBase || "Správa knowledge base", icon: BookOpen, roles: ["platform_admin"] },
    { path: "/alerts/history", label: t.nav?.alertHistory || "Alert History", icon: Bell, roles: ["platform_admin", "organization_admin", "compliance_manager"] },
    { path: "/users", label: t.nav.users, icon: Users, roles: ["platform_admin", "organization_admin"] }
  ];

  const filteredAdminItems = adminItems.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(user?.role);
  });

  const filteredModules = modules.filter(module => {
    if (!module.roles) return true;
    return module.roles.includes(user?.role);
  });

  return (
    <div className="min-h-screen transition-colors duration-200" style={{ background: 'var(--bg-main)' }} data-testid="dashboard-layout">
      {/* Mobile backdrop */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
          data-testid="mobile-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 z-50 h-screen w-64 flex flex-col transition-all duration-300 md:translate-x-0 ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ 
          background: 'var(--sidebar-bg)',
          borderRight: '1px solid var(--sidebar-border)'
        }}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div 
          className="flex h-16 items-center justify-between px-5 transition-colors duration-200"
          style={{ borderBottom: '1px solid var(--sidebar-border)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img
              src={`${process.env.PUBLIC_URL}/brand/fleetora-logo-circle-2.png`}
              alt="Fleetora"
              style={{ width: '40px', height: '40px', borderRadius: '50%' }}
            />
            <div>
              <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-inverse)' }}>Fleetora</div>
              <div style={{ fontSize: '10px', color: '#8B92A0' }}>by Aeroway</div>
            </div>
          </div>
          {/* Mobile close button */}
          <button
            className="md:hidden p-2 rounded-lg transition-colors"
            style={{ color: 'var(--text-muted)' }}
            onClick={() => setMobileMenuOpen(false)}
            data-testid="mobile-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3" data-testid="sidebar-nav">
          {/* Main Modules */}
          <div className="space-y-1">
            {filteredModules.map((module) => (
              <div key={module.key}>
                {module.standalone ? (
                  <NavLink
                    to={module.path}
                    data-testid={`nav-${module.key}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 border ${
                        isActive
                          ? "border-[var(--border-hover)]"
                          : "border-transparent"
                      }`
                    }
                    style={({ isActive }) => ({
                      background: isActive ? 'var(--sidebar-active-bg)' : 'transparent',
                      color: isActive ? 'var(--brand-accent)' : 'var(--sidebar-text)',
                      boxShadow: isActive ? '0 0 10px rgba(47, 128, 237, 0.1)' : 'none'
                    })}
                  >
                    <module.icon className="h-5 w-5" />
                    <span>{module.label}</span>
                  </NavLink>
                ) : (
                  <>
                    <button
                      onClick={() => toggleModule(module.key)}
                      data-testid={`nav-${module.key}`}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 border"
                      style={{
                        background: isModuleActive(module.paths) ? 'var(--sidebar-hover-bg)' : 'transparent',
                        color: isModuleActive(module.paths) ? 'var(--sidebar-text-active)' : 'var(--sidebar-text)',
                        borderColor: isModuleActive(module.paths) ? 'var(--border-default)' : 'transparent'
                      }}
                    >
                      <module.icon className="h-5 w-5" />
                      <span className="flex-1 text-left">{module.label}</span>
                      <span className={`transition-transform duration-200 ${expandedModules[module.key] ? 'rotate-180' : ''}`}>
                        <ChevronDown className="h-4 w-4" />
                      </span>
                    </button>
                    
                    <div className={`overflow-hidden transition-all duration-200 ${expandedModules[module.key] ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                      <div 
                        className="ml-4 mt-1 space-y-1 pl-3"
                        style={{ borderLeft: '1px solid var(--sidebar-border)' }}
                      >
                        {module.items.map((item) => (
                          <NavLink
                            key={item.path}
                            to={item.path}
                            end
                            data-testid={`nav-${item.path.slice(1)}`}
                            className={({ isActive }) =>
                              `flex items-center gap-2.5 rounded px-3 py-2 text-sm transition-all duration-200`
                            }
                            style={({ isActive }) => ({
                              background: isActive ? 'var(--sidebar-active-bg)' : 'transparent',
                              color: isActive ? 'var(--brand-accent)' : 'var(--text-muted)',
                              boxShadow: isActive ? '0 0 8px rgba(47, 128, 237, 0.1)' : 'none'
                            })}
                          >
                            <item.icon className="h-4 w-4" />
                            <span>{item.label}</span>
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Admin Section */}
          {filteredAdminItems.length > 0 && (
            <>
              <div className="my-4" style={{ borderTop: '1px solid var(--sidebar-border)' }} />
              <p 
                className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-wider"
                style={{ color: 'var(--brand-accent)', opacity: 0.5 }}
              >
                Admin
              </p>
              <div className="space-y-1">
                {filteredAdminItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.path.slice(1)}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 border ${
                        isActive
                          ? "border-[var(--border-hover)]"
                          : "border-transparent"
                      }`
                    }
                    style={({ isActive }) => ({
                      background: isActive ? 'var(--sidebar-active-bg)' : 'transparent',
                      color: isActive ? 'var(--brand-accent)' : 'var(--sidebar-text)',
                      boxShadow: isActive ? '0 0 10px rgba(47, 128, 237, 0.1)' : 'none'
                    })}
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            </>
          )}
        </nav>

        {/* User section at bottom */}
        <div style={{ borderTop: '1px solid var(--sidebar-border)' }} className="p-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-all duration-200 border border-transparent"
                style={{ 
                  color: 'var(--sidebar-text)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--sidebar-hover-bg)';
                  e.currentTarget.style.borderColor = 'var(--border-default)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-9 w-9" style={{ boxShadow: '0 0 0 2px rgba(47, 128, 237, 0.3)' }}>
                  <AvatarFallback style={{ background: 'var(--brand-primary)', color: 'var(--brand-accent-light)' }} className="text-xs">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--text-inverse)' }}>{user?.name}</p>
                  <p className="text-xs truncate" style={{ color: 'var(--brand-accent-light)', opacity: 0.7 }}>{getRoleBadge(user?.role)}</p>
                </div>
                <ChevronDown className="h-4 w-4" style={{ color: 'var(--text-muted)' }} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" style={{ background: 'var(--bg-elevated)', borderColor: 'var(--border-default)' }}>
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{user?.name}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
                {appVersionLabel && (
                  <p className="mt-1 text-[11px]" style={{ color: 'var(--text-muted)' }} data-testid="app-version">
                    {appVersionLabel}
                  </p>
                )}
              </div>
              <DropdownMenuSeparator style={{ background: 'var(--border-default)' }} />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="focus:bg-[rgba(235,87,87,0.1)]"
                style={{ color: 'var(--status-critical)' }}
                data-testid="logout-btn"
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t.auth.logout}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <main className="md:ml-64 min-h-screen transition-colors duration-200" style={{ background: 'var(--bg-main)' }}>
        {/* Critical Bulletin Banner */}
        <CriticalBulletinBanner />
        
        {/* Top bar */}
        <header 
          className="sticky top-0 z-30 flex h-14 items-center justify-between px-4 md:px-8 glass-header"
        >
          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg transition-colors"
            style={{ color: 'var(--text-primary)' }}
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-6 w-6" />
          </button>
          
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
            <span className="hidden md:inline" style={{ color: 'var(--brand-accent)', opacity: 0.7 }}>{t.platform.subtitle}</span>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            {isPlatformAdmin && (
              <Select
                value={activeOrganizationId || ALL_ORGANIZATIONS_VALUE}
                onValueChange={(value) => {
                  if (value === ALL_ORGANIZATIONS_VALUE) {
                    clearScope();
                  } else {
                    setActiveOrganization(value);
                  }
                }}
                disabled={organizationScopeLoading}
              >
                <SelectTrigger className="w-[180px] md:w-[240px]" data-testid="organization-scope-select">
                  <SelectValue placeholder={t.organizationScope.selectPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_ORGANIZATIONS_VALUE}>
                    {t.organizationScope.allOrganizations}
                  </SelectItem>
                  {organizations.map((organization) => (
                    <SelectItem key={organization.id} value={organization.id}>
                      {organization.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <BulletinNotificationBadge />
            <ThemeToggle />
            <LanguageSwitcher />
            <span className="hidden md:inline text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
              {new Date().toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
            </span>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 md:p-8" data-testid="main-content">
          {children}
        </div>
      </main>
    </div>
  );
};
